import { ChangeEvent, CSSProperties, FormEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { MotionValue } from "framer-motion";
import { Send } from "lucide-react";
import SceneTrack, { useSceneRender } from "../SceneTrack";
import SceneCanvas from "../SceneCanvas";
import { box, cbox, clamp, easeOut, jelly, lerp, place, range } from "../morph";
import { EMAIL, INFO_ITEMS, SOCIAL_LINKS } from "../../../data/contact";
import "./ContactScene.css";

/*
 * 05 · Contact — "Liquid Split & Set".
 *
 * A warm drop carrying the email address stretches, pinches and splits into
 * the form, the contact card and four social buttons, then each droplet sets
 * into a solid card. Sending runs it backwards: the fields merge into one drop
 * and it flies off. The only curved, warm, elastic section on the page.
 */

interface FormData {
    name: string;
    email: string;
    message: string;
}

interface ContactSceneProps {
    formData: FormData;
    status: string;
    /** A send is in flight. */
    sending: boolean;
    /** The last send failed; `status` holds the reason. */
    failed: boolean;
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    /** Resolves true once the message is actually delivered. */
    onSubmit: (e: FormEvent<HTMLFormElement>) => Promise<boolean>;
}

type Rect = { x: number; y: number; w: number; h: number };
/** A drop's value at rest, after the stretch (t1), and after the split (s2). */
type Path3 = [number, number, number];

/**
 * Everything that differs between the desktop scene and the portrait (phone)
 * one. The choreography is the same; only where things go changes. On a phone
 * the drop splits vertically: email pill on top, form, then the info card,
 * with the four social buttons in a row along the bottom.
 */
interface Layout {
    canvas: { width: number; height: number; navBand: number; pad: number; className: string };
    title: [number, number];
    sub: [number, number, number];
    /** Final boxes of the fields that drip into the form card. */
    fields: readonly { key: string; label: string; x: number; y: number; w: number; h: number }[];
    /** Where a field's droplet starts falling from. */
    dripY: number;
    mail: { start: Rect; stretch: [number, number]; end: Rect };
    form: Rect & { cx: Path3; cy: Path3; cw: Path3; ch: Path3 };
    info: Rect & { cx: Path3; cy: Path3; cw: Path3; ch: Path3 };
    infoText: [number, number];
    anchor: Rect;
    /** Bud start: x, y while growing, y once grown. */
    bud: [number, number, number];
    cells: { cx: number; cy: number }[];
    cell: [number, number];
    flyOff: [number, number];
}

const DESKTOP: Layout = {
    canvas: { width: 1200, height: 720, navBand: 56, pad: 48, className: "" },
    title: [96, 84],
    sub: [96, 152, 420],
    fields: [
        { key: "name", label: "Your Name", x: 124, y: 282, w: 260, h: 44 },
        { key: "email", label: "Your Email", x: 412, y: 282, w: 256, h: 44 },
        { key: "message", label: "Your Message", x: 124, y: 362, w: 544, h: 150 },
        { key: "send", label: "", x: 124, y: 538, w: 180, h: 46 },
    ],
    dripY: 236,
    mail: { start: { x: 600, y: 420, w: 420, h: 72 }, stretch: [560, 60], end: { x: 912, y: 158, w: 384, h: 52 } },
    form: { x: 96, y: 230, w: 600, h: 440, cx: [560, 360, 396], cy: [420, 420, 450], cw: [100, 140, 200], ch: [64, 90, 200] },
    info: { x: 720, y: 230, w: 384, h: 250, cx: [640, 840, 912], cy: [420, 420, 355], cw: [100, 140, 180], ch: [64, 90, 180] },
    infoText: [748, 252],
    anchor: { x: 720, y: 430, w: 384, h: 50 },
    bud: [912, 470, 500],
    cells: SOCIAL_LINKS.map((_, i) => ({ cx: 720 + 93 + (i % 2) * 198, cy: 500 + 38 + Math.floor(i / 2) * 88 })),
    cell: [186, 76],
    flyOff: [420, -300],
};

const PHONE: Layout = {
    canvas: { width: 390, height: 844, navBand: 64, pad: 0, className: "scene-canvas--phone" },
    title: [20, 100],
    sub: [20, 150, 340],
    fields: [
        { key: "name", label: "Your Name", x: 36, y: 286, w: 318, h: 40 },
        { key: "email", label: "Your Email", x: 36, y: 358, w: 318, h: 40 },
        { key: "message", label: "Your Message", x: 36, y: 430, w: 318, h: 86 },
        { key: "send", label: "", x: 36, y: 530, w: 160, h: 40 },
    ],
    dripY: 258,
    mail: { start: { x: 195, y: 470, w: 280, h: 72 }, stretch: [210, 240], end: { x: 195, y: 213, w: 350, h: 46 } },
    form: { x: 20, y: 248, w: 350, h: 336, cx: [195, 195, 195], cy: [470, 380, 416], cw: [100, 150, 200], ch: [64, 110, 200] },
    info: { x: 20, y: 596, w: 350, h: 164, cx: [195, 195, 195], cy: [470, 560, 678], cw: [100, 150, 200], ch: [64, 90, 120] },
    infoText: [36, 610],
    anchor: { x: 20, y: 728, w: 350, h: 32 },
    bud: [195, 748, 760],
    cells: SOCIAL_LINKS.map((_, i) => ({ cx: 20 + (350 / 8) * (2 * i + 1), cy: 803 })),
    cell: [80, 56],
    flyOff: [160, -340],
};

export default function ContactScene({ phone = false, ...props }: ContactSceneProps & { phone?: boolean }) {
    const L = phone ? PHONE : DESKTOP;
    return (
        <SceneTrack pin={phone ? 220 : 180} tint="255, 206, 160">
            {(p) => <ContactCanvas p={p} L={L} {...props} />}
        </SceneTrack>
    );
}

function ContactCanvas({
    p,
    L,
    formData,
    status,
    sending,
    failed,
    onChange,
    onSubmit,
}: ContactSceneProps & { p: MotionValue<number>; L: Layout }) {
    const FIELDS = L.fields;
    const CELLS = L.cells;
    const root = useRef<HTMLDivElement>(null);
    const [copied, setCopied] = useState(false);
    const els = useRef<{
        q: (k: string) => HTMLElement;
        fields: HTMLElement[];
        labels: HTMLElement[];
        items: HTMLElement[];
        budBlobs: HTMLElement[];
        budFrames: HTMLElement[];
        socials: HTMLElement[];
        emailW: number;
    } | null>(null);
    const anims = useRef<Animation[]>([]);

    useLayoutEffect(() => {
        const r = root.current;
        if (!r) return;
        const all = (s: string) => Array.from(r.querySelectorAll<HTMLElement>(s));
        const q = (k: string) => r.querySelector(`[data-ct="${k}"]`) as HTMLElement;
        const measure = () => {
            els.current = {
                q,
                fields: all(".ct-field"),
                labels: all(".ct-label"),
                items: all(".ct-info > *"),
                budBlobs: all(".ct-bud"),
                budFrames: all(".ct-bud-frame"),
                socials: all(".ct-social"),
                emailW: q("email").offsetWidth,
            };
        };
        measure();
        box(q("anchor"), L.anchor.x, L.anchor.y, L.anchor.w, L.anchor.h);
        let cancelled = false;
        document.fonts?.ready.then(() => {
            if (cancelled) return;
            measure();
            render(p.get());
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const render = (v: number) => {
        const E = els.current;
        if (!E) return;
        const { q } = E;

        q("goo").style.filter = v > 0.09 && v < 0.52 ? "url(#contact-goo)" : "none";
        const t1 = easeOut(range(v, 0.08, 0.22));
        const s2 = jelly(range(v, 0.2, 0.36));
        const s3 = jelly(range(v, 0.34, 0.52));

        // The drop stretches; its email part rises to its own pill.
        const M = L.mail;
        const ex = lerp(M.start.x, M.end.x, s2);
        const ey = lerp(M.start.y, M.end.y, s2);
        const ew = lerp(lerp(M.start.w, M.stretch[0], t1), M.end.w, s2);
        const eh = lerp(lerp(M.start.h, M.stretch[1], t1), M.end.h, s2);
        cbox(q("blob-mail"), ex, ey, ew, eh, 999);
        cbox(q("frame-mail"), ex, ey, ew, eh, 999);
        place(q("email"), ex - E.emailW / 2, ey - 13);

        // Form and info drops bulge at the ends, split off, then set into cards.
        for (const [key, D] of [["blob-form", L.form], ["blob-info", L.info]] as const) {
            const w = lerp(lerp(lerp(D.cw[0], D.cw[1], t1), D.cw[2], s2), D.w, s3);
            const h = lerp(lerp(lerp(D.ch[0], D.ch[1], t1), D.ch[2], s2), D.h, s3);
            const cx = lerp(lerp(D.cx[0], D.cx[1], t1), D.cx[2], s2);
            const cy = lerp(lerp(D.cy[0], D.cy[1], t1), D.cy[2], s2);
            cbox(q(key), lerp(cx, D.x + D.w / 2, s3), lerp(cy, D.y + D.h / 2, s3), w, h, lerp(Math.min(w, h) / 2, 14, clamp(s3)));
        }
        const set = range(v, 0.48, 0.54);
        q("frame-form").style.opacity = String(set);
        q("frame-info").style.opacity = String(set);

        // Drip: each field falls as a droplet, then spreads into an input.
        FIELDS.forEach((f, i) => {
            const el = E.fields[i];
            const lab = E.labels[i];
            const t = range(v, 0.5 + i * 0.04, 0.6 + i * 0.04);
            if (t <= 0) {
                el.style.opacity = "0";
                el.style.visibility = "hidden";
                if (lab) {
                    lab.textContent = "";
                    lab.classList.remove("is-typing");
                }
                return;
            }
            el.style.opacity = "1";
            el.style.visibility = "visible";
            const fall = jelly(range(t, 0, 0.55));
            const spread = easeOut(range(t, 0.35, 1));
            const w = lerp(14, f.w, spread);
            const h = lerp(14, f.h, spread);
            box(el, f.x + f.w / 2 - w / 2, lerp(L.dripY, f.y, fall), w, h);
            el.style.borderRadius = `${spread < 1 ? lerp(7, 10, spread) : 10}px`;
            (el.firstElementChild as HTMLElement).style.opacity = String(range(spread, 0.8, 1));
            if (lab) {
                const n = Math.floor(range(t, 0.6, 1) * f.label.length);
                lab.textContent = f.label.slice(0, n);
                lab.classList.toggle("is-typing", n > 0 && n < f.label.length);
            }
        });

        E.items.forEach((el, i) => {
            const t = range(v, 0.52 + i * 0.02, 0.58 + i * 0.02);
            el.style.opacity = String(t);
            el.style.transform = `translateY(${8 * (1 - easeOut(t))}px)`;
        });

        // Bud: four droplets grow out of the contact card and roll into place.
        const budding = v > 0.62 && v < 0.84;
        q("goo-bud").style.filter = budding ? "url(#contact-goo)" : "none";
        q("anchor").style.opacity = budding ? "1" : "0";
        E.budBlobs.forEach((b, i) => {
            const t = range(v, 0.64 + i * 0.025, 0.76 + i * 0.025);
            const c = CELLS[i];
            const social = E.socials[i];
            const frame = E.budFrames[i];
            const [cw, ch] = L.cell;
            box(social, c.cx - cw / 2, c.cy - ch / 2, cw, ch);
            box(frame, c.cx - cw / 2, c.cy - ch / 2, cw, ch);
            frame.style.opacity = String(range(v, 0.82, 0.86));
            social.style.opacity = String(range(t, 0.8, 1));
            social.style.visibility = t > 0.8 ? "visible" : "hidden";
            if (t <= 0) {
                b.style.opacity = "0";
                return;
            }
            b.style.opacity = "1";
            const a = easeOut(range(t, 0, 0.35));
            const s = jelly(range(t, 0.3, 1));
            const w = lerp(lerp(8, 30, a), cw, s);
            const h = lerp(lerp(8, 30, a), ch, s);
            const [bx, by0, by1] = L.bud;
            cbox(b, lerp(bx, c.cx, s), lerp(lerp(by0, by1, a), c.cy, s), w, h, lerp(Math.min(w, h) / 2, 12, clamp(s)));
        });
    };

    useSceneRender(p, render);

    // Sending: once delivered, the fields merge into one drop, the button packs
    // it, and it flies off. A failed send leaves the form as it was.
    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        const delivered = await onSubmit(e);
        const E = els.current;
        if (!delivered) return;
        if (!E || anims.current.length) return;
        const send = FIELDS[3];
        const bx = send.x + send.w / 2;
        const by = send.y + send.h / 2;
        const list: Animation[] = [];
        FIELDS.slice(0, 3).forEach((f, i) => {
            const dx = bx - (f.x + f.w / 2);
            const dy = by - (f.y + f.h / 2);
            list.push(
                E.fields[i].animate(
                    [
                        { transform: "none", opacity: 1, borderRadius: "10px" },
                        { transform: `translate(${dx}px,${dy}px) scale(${14 / f.w},${14 / f.h})`, opacity: 0.9, borderRadius: "999px", offset: 0.85 },
                        { transform: `translate(${dx}px,${dy}px) scale(0)`, opacity: 0 },
                    ],
                    { duration: 520, delay: i * 60, easing: "cubic-bezier(.7,0,.84,0)", fill: "forwards" }
                ),
                E.labels[i].animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, fill: "forwards" })
            );
        });
        const btn = E.fields[3];
        const label = btn.querySelector("button") as HTMLElement;
        list.push(
            label.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, delay: 420, fill: "forwards" }),
            btn.animate(
                [
                    { left: `${send.x}px`, width: `${send.w}px`, borderRadius: "10px", transform: "none", opacity: 1 },
                    { left: `${bx - 23}px`, width: "46px", borderRadius: "23px", transform: "none", opacity: 1, offset: 0.35 },
                    { left: `${bx - 23}px`, width: "46px", borderRadius: "23px", transform: "translate(0,6px) scale(.92)", opacity: 1, offset: 0.5 },
                    { left: `${bx - 23}px`, width: "46px", borderRadius: "23px", transform: `translate(${L.flyOff[0]}px,${L.flyOff[1]}px) scale(.3)`, opacity: 0 },
                ],
                { duration: 900, delay: 440, easing: "cubic-bezier(.65,0,.35,1)", fill: "forwards" }
            ),
            E.q("status").animate(
                [
                    { opacity: 0, transform: `translate(${send.x}px,${send.y - 26}px) scale(.6,1.3)` },
                    { opacity: 1, transform: `translate(${send.x}px,${send.y}px) scale(1,1)` },
                ],
                { duration: 520, delay: 1250, easing: "cubic-bezier(.34,1.56,.64,1)", fill: "forwards" }
            )
        );
        anims.current = list;
    };

    // Contact clears its status a few seconds after sending: the form drips back.
    // A failed send: show the reason under the button, where the form still is.
    useEffect(() => {
        const el = els.current?.q("status");
        if (!el || !failed || !status) return;
        el.classList.add("is-error");
        el.style.opacity = "1";
        el.style.transform = `translate(${FIELDS[3].x}px,${FIELDS[3].y + FIELDS[3].h + 12}px)`;
    }, [failed, status, FIELDS]);

    useEffect(() => {
        if (status) return;
        const el = els.current?.q("status");
        if (el) {
            el.classList.remove("is-error");
            el.style.opacity = "";
            el.style.transform = "";
        }
        anims.current.forEach((a) => a.cancel());
        anims.current = [];
        render(p.get());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const copy = () => {
        const done = () => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1400);
        };
        navigator.clipboard?.writeText(EMAIL).then(done, () => undefined);
    };

    const values: Record<string, string> = { name: formData.name, email: formData.email, message: formData.message };

    return (
        <SceneCanvas tint="255, 206, 160" marker={{ index: 5, label: "Contact" }} {...L.canvas}>
            <div ref={root} className={L === PHONE ? "ct ct--phone" : "ct"}>
                <svg width="0" height="0" className="ct-defs" aria-hidden="true">
                    <filter id="contact-goo">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="b" />
                        <feColorMatrix in="b" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -9" />
                    </filter>
                </svg>

                <h1 className="mx-abs scene-title" style={{ transform: `translate(${L.title[0]}px,${L.title[1]}px)` }}>
                    Get In <span>Touch</span>
                </h1>
                <p className="mx-abs scene-sub" style={{ transform: `translate(${L.sub[0]}px,${L.sub[1]}px)`, width: L.sub[2] }}>
                    {"Let's work together on your next project"}
                </p>

                {/* Only the drops' fills are filtered; text sits above, sharp. */}
                <div className="ct-goo" data-ct="goo">
                    <div className="ct-blob" data-ct="blob-form" />
                    <div className="ct-blob" data-ct="blob-info" />
                    <div className="ct-blob" data-ct="blob-mail" />
                </div>
                <div className="ct-goo" data-ct="goo-bud">
                    <div className="ct-blob ct-anchor" data-ct="anchor" />
                    {SOCIAL_LINKS.map((s) => (
                        <div key={s.name} className="ct-blob ct-bud" />
                    ))}
                </div>

                <div className="ct-frame ct-frame--mail" data-ct="frame-mail" />
                <div className="ct-frame" data-ct="frame-form" style={{ left: L.form.x, top: L.form.y, width: L.form.w, height: L.form.h }} />
                <div className="ct-frame" data-ct="frame-info" style={{ left: L.info.x, top: L.info.y, width: L.info.w, height: L.info.h }} />
                {SOCIAL_LINKS.map((s, i) => (
                    <div key={s.name} className="ct-frame ct-bud-frame" style={{ "--i": i } as CSSProperties} />
                ))}

                <div className="mx-abs ct-email mono" data-ct="email">
                    <span>{EMAIL}</span>
                    <button type="button" className="ct-copy" onClick={copy}>
                        {copied ? "Copied" : "Copy"}
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Honeypot for bots — never shown to people. */}
                    <input
                        type="checkbox"
                        name="botcheck"
                        className="ct-botcheck"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                    />
                    {FIELDS.map((f) =>
                        f.key === "send" ? (
                            <div key={f.key} className="ct-field ct-field--send">
                                <button type="submit" disabled={sending}>
                                    <span>{sending ? "Sending…" : "Send Message"}</span>
                                    <Send size={15} strokeWidth={2} />
                                </button>
                            </div>
                        ) : (
                            <div key={f.key}>
                                <label
                                    className="mx-abs ct-label"
                                    htmlFor={`ct-${f.key}`}
                                    style={{ transform: `translate(${f.x}px,${f.y - 24}px)` }}
                                    aria-label={f.label}
                                />
                                <div className="ct-field">
                                    {f.key === "message" ? (
                                        <textarea
                                            id={`ct-${f.key}`}
                                            name={f.key}
                                            value={values[f.key]}
                                            onChange={onChange}
                                            required
                                            placeholder="Tell me about your project..."
                                        />
                                    ) : (
                                        <input
                                            id={`ct-${f.key}`}
                                            name={f.key}
                                            type={f.key === "email" ? "email" : "text"}
                                            value={values[f.key]}
                                            onChange={onChange}
                                            required
                                            placeholder={f.key === "email" ? "john@example.com" : "John Doe"}
                                        />
                                    )}
                                </div>
                            </div>
                        )
                    )}
                </form>
                <div className="mx-abs ct-status" data-ct="status" role="status">
                    {status}
                </div>

                <div className="mx-abs ct-info" style={{ transform: `translate(${L.infoText[0]}px,${L.infoText[1]}px)` }}>
                    <h3>Contact Information</h3>
                    {INFO_ITEMS.map(({ label, value, Icon }) => (
                        <div key={label} className="ct-item">
                            <span className="ct-icon">
                                <Icon size={15} strokeWidth={1.8} />
                            </span>
                            <p>
                                <small>{label}</small>
                                {value}
                            </p>
                        </div>
                    ))}
                </div>
                {SOCIAL_LINKS.map(({ name, url, Icon }, i) => (
                    <a
                        key={name}
                        className="ct-social"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ "--i": i } as CSSProperties}
                    >
                        <span className="ct-social-icon">
                            <Icon size={16} />
                        </span>
                        {name}
                    </a>
                ))}
            </div>
        </SceneCanvas>
    );
}
